import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import ChannelAvatar from '../general-components/channel-avatar';
import AvatarBubbleRow from '../general-components/avatar-bubble-row';
import Badges from '../general-components/badges';
import * as Alerts from '../general-components/alert-service';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import EditEllipses from '../general-components/edit-ellipses';
import useHome from '../../hooks/use-home';
import { GreenroomState } from '../../stores/types/types';
import EventMenu from '../general-components/event-menu';
import { whichChannelLogoToUse } from '../../utils/utils';

interface MeetingRowProps {
	start_time: number;
	end_time: number;
	logo_white: string;
	logo_color?: string;
	title: string;
	channel_name: string;
	invites: any[];
	showSessionId: number;
	show_session_type: string;
	settings_json?: string;
	forPresenter?: boolean;
	isLive?: boolean;
	meeting: number;
	live_chat: number;
	events_session_uuid: number;
	inShowDetailsView?: boolean;
	canEdit?: boolean;
	modifyEvent?: (event: any, type: string) => void;
	onClick: (props: MeetingRowProps) => void;
	isWaiting?: boolean;
	buttonDisabled?: boolean;
	connected?: boolean;
	previouslyVisited: boolean;
	standaloneGreenroomProducer?: boolean;
	onEditClicked: (meeting: number) => void;
	language: string;
}

function meetingTime(timestamp: number): string {
	return new Date(timestamp).toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit' });
}

const now = new Date();
const date = now.getDate();
const month = now.getMonth();
const year = now.getFullYear();

function eventDayContext(timestamp: number): string {
	const eventDate = new Date(timestamp);
	if (
		date === eventDate.getDate()
		&& month === eventDate.getMonth()
		&& year === eventDate.getFullYear()
	) {
		return 'Today';
	}
	return `${eventDate.getDate()} ${eventDate.toLocaleDateString(navigator.language, { month: 'short' })}`;
}

export default function MeetingRow(props: MeetingRowProps) {
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const {
		meeting,
		live_chat,
		events_session_uuid,
		start_time,
		end_time,
		logo_white,
		logo_color,
		channel_name,
		title,
		invites,
		onClick,
		connected = false,
		buttonDisabled = false,
		forPresenter,
		isLive,
		inShowDetailsView = false,
		canEdit = false,
		modifyEvent,
		show_session_type,
		language,
	} = props;

	const {
		filteredMeetingsByChannel,
		handleEnterShowDetails,
		showDetailsButtonDisabled,
	} = useHome();

	function attemptEntry() {
		if (!connected || buttonDisabled || showDetailsButtonDisabled) {
			return;
		}

		if (forPresenter) {
			if (isLive) {
				onClick(props);
			} else {
				Alerts.showAlert({
					message: 'Waiting for producer',
					description: "You'll be able to join when the producer is present",
					duration: 2000,
					type: 'neutral',
				});
			}
		} else if (!inShowDetailsView) {
			if (meeting) {
				handleEnterShowDetails(
					filteredMeetingsByChannel().find(
						(event: any) => event.meeting === meeting,
					),
				);
			} else if (events_session_uuid) {
				handleEnterShowDetails(
					filteredMeetingsByChannel().find(
						(event: any) => event.uuid === events_session_uuid && event.language === language,
					),
				);
			} else if (live_chat) {
				handleEnterShowDetails(
					filteredMeetingsByChannel().find(
						(event: any) => event.live_chat === live_chat,
					),
				);
			}
		} else {
			onClick(props);
		}
	}

	const time = useMemo(() => meetingTime(start_time), [start_time]);

	const day = useMemo(() => eventDayContext(start_time), [start_time]);

	return (
		<div className="event-row" onClick={attemptEntry}>
			<div className="event-time">
				<span className="timestamp">
					{time}
				</span>
				<span className="day">
					{day}
				</span>
			</div>
			{!inShowDetailsView && <div className="channel-logo">
				<ChannelAvatar
					channelName={channel_name}
					logoWhite={whichChannelLogoToUse(logo_white, logo_color)}
				/>
			</div>}
			<div className="event-titles">
				<h4>{title}</h4>
				<Badges
					start_time={start_time}
					end_time={end_time}
					show_session_type={show_session_type}
				/>
			</div>
			<div className={classNames('presenters-actions', invites && invites.length ? 'show-border' : '')}>
				<AvatarBubbleRow invites={invites} size={'thirtytwo'} alignEnd={false} expandable={!inShowDetailsView} />
				<div className="event-actions">
					{host && canEdit && (
						<>
							{props.inShowDetailsView ? (
								<EditEllipses
									editCallback={() => {
										if (modifyEvent) {
											modifyEvent(props.showSessionId, 'edit');
										}
									}}
									deleteCallback={() => {
										if (modifyEvent) {
											modifyEvent(props.showSessionId, 'delete');
										}
									}}
									showEditDelete={true}
								></EditEllipses>
							) : (
								<EventMenu event={props} />
							)}
						</>
					)}
					<button className={classNames('details-button transparent', { 'height-full': !host || !canEdit, disabled: buttonDisabled })}>
						<Icon name={ICONS.ARROW_RIGHT} color={COLORS.WHITE} size={15} />
					</button>
				</div>
			</div>
		</div>
	);
}
